import { Backdrop, Box, Button, Chip, Divider, Fade, Modal, Paper, Skeleton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderOpen, faTags, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useLang } from '../../../../language';

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 720,
    maxWidth: '94%',
    maxHeight: '88vh',
    overflowY: 'auto' as 'auto',
    overflowX: 'hidden' as 'hidden',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
}

/**
 * Modal de detall d'un curs del catàleg (accion/modulo=mostrar_detalle_curso&id=N).
 *
 * El contingut procedeix SOLO de la resposta del detall; la fila del listat
 * (`curso`) només actua de disparador (id) mentre es carrega.
 */
export const ModalCurso = ({ curso, detalle, cargando, onClose }: any) => {

    const Lang = useLang()

    const cursoActual = detalle || {}

    // En el detalle la categoría puede llegar como ID numérico (`cc_categoria`) si
    // el WS no la traduce a texto (en el listado sí viene como texto). En ese caso
    // no se muestra el chip.
    const categoriaTexto = cursoActual.categoria && !/^\d+$/.test(String(cursoActual.categoria)) ? cursoActual.categoria : ''

    return (
        <Modal
            open={!!curso}
            onClose={onClose}
            aria-labelledby="modal-curso"
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 200,
            }}
        >
            <Fade in={!!curso}>

                <Paper sx={modalStyle}>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>

                        <Typography variant='h6' className='fw-500 my-0'>
                            {Lang('CURSO_DETALLE')}
                        </Typography>

                        <Button
                            size="small"
                            color="inherit"
                            onClick={onClose}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </Button>
                    </Box>

                    {
                        curso &&
                        <Box className='p-3'>

                            {
                                cargando &&
                                <Box>
                                    <Skeleton variant="text" animation="wave" width="75%" sx={{ mb: 1 }} />
                                    <Skeleton variant="text" animation="wave" width="30%" sx={{ mb: 2 }} />
                                    <Skeleton variant="rectangular" animation="wave" sx={{ borderRadius: 1.5, mb: 2, height: 120 }} />
                                    <Skeleton variant="text" animation="wave" />
                                    <Skeleton variant="text" animation="wave" />
                                    <Skeleton variant="text" animation="wave" width="60%" />
                                </Box>
                            }

                            {
                                !cargando && !cursoActual?.id &&
                                <Box className='p-3'>
                                    <Typography variant='body1' className='my-0' sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                                        {Lang('SIN_RESULTADOS')}
                                    </Typography>
                                </Box>
                            }

                            {
                                !cargando && cursoActual?.id &&
                                <Box>

                                    {/* Capçalera: títol + categoria */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                        {
                                            categoriaTexto &&
                                            <Chip
                                                icon={<FontAwesomeIcon icon={faTags} style={{ fontSize: 12 }} />}
                                                label={categoriaTexto}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        }
                                        <Typography variant='h6' className='fw-500 my-0' sx={{ wordBreak: 'break-word', minWidth: 0 }}>
                                            {cursoActual.titulo}
                                        </Typography>
                                    </Box>

                                    {
                                        cursoActual.temas?.length > 0 &&
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                                            {
                                                cursoActual.temas.map((tema: string, indice: number) => (
                                                    <Chip key={indice} label={tema} size="small" />
                                                ))
                                            }
                                        </Box>
                                    }

                                    {
                                        cursoActual.descripcion &&
                                        <Paper variant="outlined" className='p-3 mb-2' sx={{ borderRadius: 2, bgcolor: 'action.hover' }}>
                                            <Typography
                                                variant='body2'
                                                dangerouslySetInnerHTML={{ __html: cursoActual.descripcion }}
                                            />
                                        </Paper>
                                    }

                                    {
                                        cursoActual.fichero &&
                                        <Box className='mt-2 pt-1'>
                                            <Divider className='mb-2' />
                                            <Button
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                href={cursoActual.fichero}
                                                target="_blank"
                                                rel="noreferrer"
                                                startIcon={<FontAwesomeIcon icon={faFolderOpen} />}
                                            >
                                                {Lang('VER_DETALLE')}
                                            </Button>
                                        </Box>
                                    }

                                </Box>
                            }

                        </Box>
                    }

                </Paper>

            </Fade>
        </Modal>
    )
}

export default ModalCurso